import { isPrerendering } from "../utils/prerender";
import { useState, useEffect, useCallback } from "react";
import subscriptionApi, {
  SubscriptionPlan,
  UserSubscription,
  UserCredit,
  CreditBalance,
} from "../services/subscriptionApi";
import { useLanguage } from "../contexts/LanguageContext";
import {
  fetchWithCache,
  fetchWithDedup,
  getCached,
  isCacheValid,
  invalidateCacheByPrefix,
} from "../utils/requestCache";

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface UseSubscriptionPlansOptions {
  autoFetch?: boolean;
}

interface UseSubscriptionPlansReturn {
  plans: SubscriptionPlan[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch subscription plans (public)
 */
export function useSubscriptionPlans(
  options: UseSubscriptionPlansOptions = {}
): UseSubscriptionPlansReturn {
  const { autoFetch = true } = options;
  const { currentLanguage } = useLanguage();

  const cacheKey = `plans_${currentLanguage}`;

  const [plans, setPlans] = useState<SubscriptionPlan[]>(() => {
    return getCached<SubscriptionPlan[]>(cacheKey, CACHE_DURATION) || [];
  });
  const [loading, setLoading] = useState(
    () => !isCacheValid(cacheKey, CACHE_DURATION)
  );
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(
    async (forceRefresh = false) => {
      if (forceRefresh) {
        invalidateCacheByPrefix("plans_");
      }

      if (isPrerendering()) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchWithCache(
          cacheKey,
          () => subscriptionApi.getPlans(), //subscriptionApi.getPlans(currentLanguage)
          CACHE_DURATION
        );
        // Sort by sortOrder
        const sortedPlans = [...data].sort(
          (a, b) =>
            (a.sortOrder || a.displayOrder || 0) -
            (b.sortOrder || b.displayOrder || 0)
        );
        setPlans(sortedPlans);
      } catch (err: any) {
        setError(err.message || "Failed to load subscription plans");
      } finally {
        setLoading(false);
      }
    },
    [currentLanguage, cacheKey]
  );

  useEffect(() => {
    let cancelled = false;

    if (autoFetch) {
      fetchPlans().then(() => {
        if (cancelled) return;
      });
    }

    return () => {
      cancelled = true;
    };
  }, [autoFetch, fetchPlans, currentLanguage]);

  return { plans, loading, error, refetch: () => fetchPlans(true) };
}

interface UseMySubscriptionReturn {
  subscription: UserSubscription | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const MY_SUBSCRIPTION_DEDUP_KEY = "my_subscription";

/**
 * Hook to fetch current user's subscription (requires auth) - deduplicated
 */
export function useMySubscription(): UseMySubscriptionReturn {
  const [subscription, setSubscription] = useState<UserSubscription | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithDedup(MY_SUBSCRIPTION_DEDUP_KEY, () =>
        subscriptionApi.getMySubscription()
      );
      setSubscription(data);
    } catch (err: any) {
      setError(err.message || "Failed to load subscription");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchSubscription().then(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [fetchSubscription]);

  return { subscription, loading, error, refetch: fetchSubscription };
}

interface UseCreditsReturn {
  balance: CreditBalance | null;
  creditsByService: UserCredit[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const CREDITS_BALANCE_DEDUP_KEY = "credits_balance";
const CREDITS_BY_SERVICE_DEDUP_KEY = "credits_by_service";

/**
 * Hook to fetch user's credits (requires auth) - deduplicated
 */
export function useCredits(): UseCreditsReturn {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [creditsByService, setCreditsByService] = useState<UserCredit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCredits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [balanceData, byServiceData] = await Promise.all([
        fetchWithDedup(CREDITS_BALANCE_DEDUP_KEY, () =>
          subscriptionApi.getCreditBalance()
        ),
        fetchWithDedup(CREDITS_BY_SERVICE_DEDUP_KEY, () =>
          subscriptionApi.getCreditsByService()
        ),
      ]);
      setBalance(balanceData);
      setCreditsByService(byServiceData);
    } catch (err: any) {
      setError(err.message || "Failed to load credits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchCredits().then(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [fetchCredits]);

  return {
    balance,
    creditsByService,
    loading,
    error,
    refetch: fetchCredits,
  };
}
